import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import FormGroup from '@material-ui/core/FormGroup';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import NavBar from '../NavBar';
import Drawer from '../Drawer';
import InfoPanel from '../InfoPanel';
import MainPanel from '../MainPanel';
import AlarmCover from '../AlarmCover';
import { styles } from '../../styles/dashboard-styles';
import { DataMap, DataMapContainer } from '../../classes';
import { fetchAllMonitors, fetchUserSettings } from '../../utils/api';
import { formatDateTime } from '../../utils/format-time';
import { buildDataMapContainer } from '../../utils/handle-data';
import * as alertUtils from '../../utils/alerts';
import * as handlers from './utils/handlers';
import dashboardTitle from '../../images/msd-dashboard-logo.png'
import { updateAlertCutoff, updateSettings } from 'src/libs/update-lib';

class Dashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      expand: false,
      dataMapContainer: [],
      alerts: null,
      timesDataMap: '',
      friendlyName: "",
      rangeSetting: 86400,
      chartType: "area",
      refreshRate: "60",
      scale: "auto",
      domainMin: "0.1",
      domainMax: "50",
      layout: "list",
      cols: null,
      height: null,
      dark: true,
      updateNowText: "update now",
      sortOrder: "alerts",
      alarmSoundOn: false,
      clearAllAlertsDisabled: false,
      panel: "card",
      detailsMonitorId: null,
      hideCover: 0
    };
  }

  handleIncrementHideCover = () => {
    this.setState({
      hideCover: 1
    })
  }
  handleSetMonitorId = (e, monitorId) => {
    if(monitorId){
      this.setState({
        detailsMonitorId: monitorId,
        panel: "details"
      })
    } else{
       this.setState({
        detailsMonitorId: null,
        panel: "card"
      }) 
    }
  }

  handleDeleteAlertsFromState = (monitorId) => {
    let alerts = this.state.alerts;
    alerts[monitorId] = null;
    this.setState({
      alerts,
    })
  }
  handleClearAllAlerts = () => {
    Object.keys(this.state.alerts).forEach(key => {
      alertUtils.processAllAlerts(this.state.alerts[key], key, this.updateAlertInState, true);
    })
    this.setState({
      clearAllAlertsDisabled: true
    })
  }

  setClearAllAlertsDisabled = (alerts) => {
    const activeAlerts = alertUtils.checkForActiveAlerts(alerts);
    this.setState({
      clearAllAlertsDisabled: !activeAlerts
    })
  }

  updateAlertInState = (alertToUpdate) => {
    const alerts = this.state.alerts;
    const monitorsAlerts = alerts[alertToUpdate.monitorId];
    const updatedAlerts = monitorsAlerts.map(alert => {
      if(alertToUpdate.time === alert.time) {
        alert = alertToUpdate.alert;
      }
      return alert;
    })
    this.setState({
      alerts,
    })
    this.setClearAllAlertsDisabled(alerts);
    this.handleAlarmSoundOn(alerts);
  }

  handleAlarmSoundOn = (alerts) => {
    if(alerts) {
      const alarmSoundOn = Object.keys(alerts).some(key => {
        return alerts[key].some(alert => {
          return alert.cleared === false;
        })
      })
      this.setState({
        alarmSoundOn,
      })
    }
  }

  handleTheme = (event) => {
      this.props.handleThemeChange(!this.state.dark);
      this.setState({ dark: event.target.checked });
    }

  handleDrawerOpen = () => {
    this.setState({ open: true });
  };

  handleDrawerClose = () => {
    this.setState({ open: false, expand: false });
  };

  handleClickListItem = () => {
    if(!this.state.expand) {
      this.setState({
        open: true
      })
    }
    this.setState({
      expand: !this.state.expand
    })
  }

  handleInputChange = (e) => {
    const name = e.target.name;
    let cols = this.state.cols;
    const value = name === "cols" ? parseInt(e.target.value) : e.target.value;
    if(name === "layout") {
      if(value === "grid" && cols === 1){
        cols = 2;
        this.setState({
          [name]: value,
          cols,
        });
      } else if(value === "list") {
        cols = 1;
        this.setState({
          [name]: value,
          cols,
        });
      } else if(value === "slideshow") {
        cols = 2;
        this.setState({
          [name]: value,
          cols,
        })
      } else {
        this.setState({
          [name]: value,
          cols,
        });
      }
    } else {
      this.setState({[name]: value});
    }
    updateSettings([{field: name, value,}])
  }

  handleAlertCutoffChange = (monitorId, cutoff) => {
    updateAlertCutoff(monitorId, cutoff).then(res => {
      if(res.status) {
        this.fetchMonitors();
      }
    })
  }

  handleApplySettings = (
    chartType,
    rangeSetting,
    refreshRate,
    dataMapContainer
  ) => {
    this.setState({
      chartType,
      rangeSetting,
      refreshRate,
      refreshCounter: parseInt(refreshRate),
      dataMapContainer,
    })
  }

  onSiteChange = (dataMapContainer) => {
    this.setState({
      dataMapContainer,
    })
  }

  fetchMonitors = () => {
    return fetchAllMonitors(this.state.rangeSetting)
    .then(data => {
      const alerts = alertUtils.tagCriticalAlerts(data.alerts);
      const dataMapContainer = buildDataMapContainer(data.monitors);
      const nextRequestStartDate = dataMapContainer[0].getLastKey();
      this.setState({
        dataMapContainer,
        lastUpdate: formatDateTime(Date.now()/1000, true),
        alerts: alertUtils.sortAlertsByMonitorId(alertUtils.reduceAlertsToDateRange(alerts, this.state.rangeSetting)),
        allAlerts: alertUtils.sortAlertsByMonitorId(alerts),
        updateNowText: "update now"
      })
    })
  }

  fetchMonitorsAndSetAlarmSound = () => {
    this.fetchMonitors().then(() => {
      this.handleAlarmSoundOn(this.state.alerts);
      this.setClearAllAlertsDisabled(this.state.alerts);
    })
  }

  refreshMonitors = () => {
    this.setState({
      updateNowText: "updating..."
    })
    clearInterval(this.interval);
    this.fetchMonitorsAndSetAlarmSound();
    this.interval = setInterval(() => {
      this.fetchMonitorsAndSetAlarmSound();
    }, parseInt(this.state.refreshRate) * 1000);

  }

  resizeHeight = () => {
    const multipler = this.state.layout === "slideshow" ? 0.23 : 0.13
    const newHeight = window.innerHeight * multipler;
    this.setState({
      height: newHeight
    })
  }

  setUserSettings = (settings) => {
    const { layout, refreshRate, rangeSetting, scale, domainMin, domainMax } = settings;
    const cols = layout === "list" ? 1 : 2;
    this.setState({
      layout,
      refreshRate,
      rangeSetting,
      cols,
      scale,
      domainMin,
      domainMax,
    })
  }
  
  componentDidMount() {
    fetchUserSettings().then(res => {
      this.setUserSettings(res);
      this.resizeHeight();
    })
    this.props.handleThemeChange(true);
    this.refreshMonitors();
    window.addEventListener("resize", this.resizeHeight.bind(this));
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  render() {
    if(this.state.alerts !== undefined) {
      alertUtils.checkForActiveAlerts(this.state.alerts);
    }
    const { classes } = this.props;
    const headerTitle = () => {
      const titleClass = window.innerWidth > 600 ? "headerTitle" : "headerTitleMobile";
      return (
      <div className={classes.titleContainer}>
        <img src={dashboardTitle} className={classes[titleClass]}/>
      </div>
      );
    }
    return (
      <div className={classes.root}>

        <CssBaseline />
        <NavBar
          title={headerTitle()}
          open={this.state.open}
          handleDrawerOpen={this.handleDrawerOpen}
          handleLogout={this.props.handleLogout}
          alarmSoundOn={this.state.alarmSoundOn}
          handleClearAllAlerts={this.handleClearAllAlerts}
          clearAllAlertsDisabled={this.state.clearAllAlertsDisabled}
        />
        <Drawer
          open={this.state.open}
          drawerClose={this.handleDrawerClose}
          onApplySettings={this.handleApplySettings}
          monitorId={this.state.monitorId}
          rangeSetting={this.state.rangeSetting}
          chartType={this.state.chartType}
          refreshRate={this.state.refreshRate}
          scale={this.state.scale}
          domainMin={this.state.domainMin}
          domainMax={this.state.domainMax}
          layout={this.state.layout}
          cols={this.state.cols}
          onChange={this.handleInputChange}
          onCheck={this.handleCheck}
          dataMapContainer={this.state.dataMapContainer}
          onSiteChange={this.onSiteChange}
        />
        <main className={classes.content}>
          <InfoPanel {...this.state} handleInputChange={this.handleInputChange} handleSetMonitorId={this.handleSetMonitorId} refreshMonitors={this.refreshMonitors} />
          <MainPanel {...this.state} {...this} data={this.state.dataMapContainer} />
        </main>
      </div>
    );
  }
}

Dashboard.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Dashboard);



// WEBPACK FOOTER //
// ./src/components/Dashboard/Dashboard.js